import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCRefModalProps, RCCServicesApi, RCCTextArea, RCCTooltip } from "react-common-components";
import { useFieldArray, useForm } from "react-hook-form";
import Audit from "src/components/Audit";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import Select from "src/components/Select";
import Tabs from "src/components/Tabs";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { v4 } from "uuid";
import { IPatba002, IRefParametersModal } from "./interface";
import { parameterSchema } from "./schemas";

const ModalParameter: React.ForwardRefRenderFunction<IRefParametersModal> = (_,ref) => {
	
	const defaultParameter:IPatba002 = {
		paa002Id:0,
		paa001Id:0,
		paa002Parametro:"",
		paa002Descricao:"",
		paa002Tipo:"",
		options:[]
	};

	const controllerUrl = "Parameters/"; 

	const { translate} = useTranslation();
	const { addToast} = RCCHookToast();
	
	const [isAdd, setIsAdd] = useState(false);
	const [parameter, setParameter] = useState<IPatba002>(defaultParameter);
	const [onCloseFunction, setonCloseFunction] = useState<()=>void>();

	const {
		register,
		control,
		formState:{errors},
		setError,
		clearErrors,
		reset,
		handleSubmit,
		setValue
	}= useForm<IPatba002>();

	const {fields:options,append,remove} = useFieldArray({name:"options",control});

	const modalRef = useRef<RCCRefModalProps>(null);
    
	const addParameter = (groupId:number,onClose:()=>void) => {
		setParameter({...defaultParameter,paa001Id:groupId});
		setIsAdd(true);
		reset();
		setonCloseFunction(() => onClose);
		modalRef.current?.openModal();
	};

	const submitParameter = async(data: any) => {
		try{
			clearErrors();
			modalRef.current?.setLoadingButton();
			const dataForm = data as IPatba002;
			await parameterSchema(translate).validate({...dataForm}, {
				abortEarly: false
			});

			if(dataForm.paa002Tipo === "2"){
				dataForm.options = [];
			}else{
				dataForm.paa002Sqllov = "";
				dataForm.options = dataForm.options.map((x,idx) => ({...x,paa003Opcao:idx+1}));
			}

			if(isAdd){
				await RCCServicesApi.postRequest({
					method: `${controllerUrl}InsertParameter`,
					body: {...dataForm}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: `${controllerUrl}UpdateParameter`,
					body: {...dataForm}
				});
			}

			if(onCloseFunction)
				onCloseFunction();
	
			addToast({
				color: "green",
				description: isAdd ? translate("Parâmetro adicionado com sucesso") : translate("Parâmetro alterado com sucesso"),
				title: translate("Sucesso")
			});
	
			modalRef.current?.resetLoadingButton();
			modalRef.current?.closeModal();
	
		}catch(err){
			modalRef.current?.resetLoadingButton();
			if(getValidationErrors(err,setError)){
				addToast({
					color: "yellow",
					description: translate("Verifique os campos e tente novamente"),
					title: translate("Atenção")
				});
				return;
			}
	
			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar parâmetro") : translate("Erro ao alterar parâmetro"), addToast);
		}
	};

	const parameterRequest = async(parameterId:number) =>
	{
		try {
			const {data} = await RCCServicesApi.getRequest({
				method: `${controllerUrl}SelectParameter`,
				urlParameters:{parameterId}
			});
			
			setParameter(data);
			setValue("options",data.options);

		} catch (error) {
			standardErrorMessages(error, translate("Erro ao buscar parâmetro"), addToast);
		}
	};

	const changeParameter = async (parameterId:number,onClose:()=>void) => {
		setIsAdd(false);
		reset();
		await parameterRequest(parameterId);
		setonCloseFunction(() => onClose);
		modalRef.current?.openModal();
	};

	const returnError = () =>
	{
		const err = errors.options as any;

		return err?.message;
	};

	useImperativeHandle(ref, () => ({
		addParameter,
		changeParameter
	}));

	return (
		<Modal
			ref={modalRef}
			header={{
				title:isAdd ? translate("Adicionar parâmetro"):translate("Alterar parâmetro"),
				subtitle:!isAdd ? `${translate("Parâmetro")}: ${parameter?.paa002Descricao}`:""
			}}
			styleInline={ModalSide}
			onSubmit={handleSubmit(submitParameter)}
		>
			<Tabs
				name="parametersTabs"
				tabInit={0}
				options={[
					{
						label:translate("Dados"),
						content: 
						<RCCGrid container justifyContent="center">
							<RCCGrid item sm={10}>
								<RCCGrid container item justifyContent="flex-end">
									<Audit data={parameter} />
								</RCCGrid>
								<RCCGrid item>
									<input 
										{...register("paa002Id")} 
										name="paa002Id"
										value={parameter.paa002Id}
										type="hidden"
									/>
									<input 
										{...register("paa001Id")} 
										name="paa001Id"
										value={parameter.paa001Id}
										type="hidden"
									/>
									<RCCInput 
										register={register}
										name="paa002Parametro"
										label={translate("Parâmetro")}
										error={errors.paa002Parametro}
										defaultValue={parameter?.paa002Parametro}
										readOnly={!isAdd}
										required
										maxLength={4}
									/>
								</RCCGrid>
								<RCCGrid item>
									<RCCInput 
										register={register}
										name="paa002Descricao"
										label={translate("Descrição")}
										error={errors.paa002Descricao}
										defaultValue={parameter?.paa002Descricao}
										required
										maxLength={200}
									/>
								</RCCGrid>
								<RCCGrid item>
									<RCCTextArea
										name="paa002Explicacao"
										register={register}
										label={translate("Explicação")}
										defaultValue={parameter.paa002Explicacao}
										maxLength={4000}
									/>	
								</RCCGrid>
								<RCCGrid item>
									<Select 
										control={control}
										name="paa002Tipo"
										label={translate("Tipo")}
										error={errors.paa002Tipo}
										defaultValue={parameter.paa002Tipo.toString()||"1"}
										required
										options={[
											{value:"1",label:translate("Lista de opções")},
											{value:"2",label:translate("Valor")}
										]}
										onChangeCapture={(e) => {
											setParameter((prevState) => ({...prevState,paa002Tipo:e.value}));
										}}
									/>
								</RCCGrid>
								<RCCGrid item>
									<RCCInput 
										register={register}
										name="paa002Valordefault"
										label={translate("Valor padrão")}
										error={errors.paa002Valordefault}
										defaultValue={parameter.paa002Valordefault}
										maxLength={240}
									/>
								</RCCGrid>
								{parameter.paa002Tipo.toString() === "2" &&
									<RCCGrid item>
										<RCCTextArea
											register={register}
											name="paa002Sqllov"
											label="SQL Lov"
											error={errors.paa002Sqllov}
											defaultValue={parameter.paa002Sqllov}
											maxLength={4000}
										/>
									</RCCGrid>
								}
							</RCCGrid>
						</RCCGrid>
					},
					{
						label:translate("Opções"),
						hidden:parameter.paa002Tipo.toString() !== "1",
						content: 
						<RCCGrid container justifyContent="center">
							<RCCGrid item sm={10}>
								<RCCGrid container justifyContent="space-between" alignContent="flex-end" wrap="nowrap">
									{errors.options &&
										<RCCGrid container item alignItems="center">	
											<RCCIcon name="errorOutline" size={19} color="var(--red)"/>
											<Title color="red" size="sm">{returnError()}</Title>
										</RCCGrid>
									}	
									<RCCGrid container item justifyContent="flex-end">
										<RCCTooltip
											text={translate("Nova opção")}
											color="purple"
										>
											<RCCButton
												icon={{name: "plus"}}
												color="purple"
												size="sm"
												layout="isRounded"
												onClick={() => {
													clearErrors("options");
													append({id:v4(),paa003Descricao:"",paa003Opcao:0,paa003Valor:""});
												}}
											/>
										</RCCTooltip>
									</RCCGrid>
								</RCCGrid>
								<RCCGrid item>
									{options.map((opt,index) => (
										<RCCGrid key={opt.id} item container alignItems="baseline">
											<RCCGrid item xs={12} sm={5}>
												<RCCInput 
													register={register}
													name={`options.${index}.paa003Descricao` as const}
													label={translate("Descrição")}
													error={errors.options && errors.options[index]?.paa003Descricao}
													defaultValue={opt.paa003Descricao}
													maxLength={40}
													required
												/>
											</RCCGrid>
											<RCCGrid item xs={10} sm={5} 
												styleInline="&.MuiGrid-root {@media(min-width: 600px){padding-left: 8px}}"
											>
												<RCCInput 
													register={register}
													name={`options.${index}.paa003Valor` as const}
													label={translate("Valor")}
													error={errors.options && errors.options[index]?.paa003Valor}
													defaultValue={opt.paa003Valor}
													maxLength={240}
													required
												/>
											</RCCGrid>
											<RCCGrid container item xs={2} justifyContent="flex-end">
												<RCCTooltip
													text={translate("Excluir opção")}
													color="red"
												>
													<RCCButton
														icon={{name: "trash"}}
														color="red"
														size="sm"
														layout="isRounded"
														onClick={() => remove(index)}
													/>
												</RCCTooltip>
											</RCCGrid>
											<RCCGrid item xs={12} >
												<RCCTextArea
													register={register}
													name={`options.${index}.paa003Explicacao` as const}
													label={translate("Explicação")}
													error={errors.options && errors.options[index]?.paa003Explicacao}
													defaultValue={opt.paa003Explicacao}
													maxLength={4000}
												/>
											</RCCGrid>
											<hr/>
										</RCCGrid>
									))}
								</RCCGrid>
							</RCCGrid>
						</RCCGrid>
					}
				]}
			/>
		</Modal>
	);

};

export default forwardRef(ModalParameter);
